import {
  afterNextRender,
  Component,
  contentChildren,
  DestroyRef,
  effect,
  inject,
  output,
  signal,
  viewChild
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { filter } from 'rxjs/operators';
import { TranslatePipe as NgxTranslatePipe } from '@ngx-translate/core';

import { ScrollableDirective } from '../scroll/scrollable.directive';
import { HorizontalScrollDirection } from '../../domain/direction';
import { IconComponent } from '../icon/icon.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { TabsComponent } from './tabs.component';

import type { EffectCleanupRegisterFn, OutputEmitterRef, Signal, WritableSignal } from '@angular/core';
import type { Subscription } from 'rxjs';
import type { TabComponent } from './tab.component';
import type { TabStatusChangeEvent } from './tabs.domain';

@Component({
  selector: 'td-wb-tabs-scroll',
  templateUrl: 'tabs-scroll.component.html',
  styleUrls: ['tabs-scroll.component.scss'],
  imports: [IconButtonComponent, IconComponent, NgxTranslatePipe, ScrollableDirective]
})
export class TabsScrollComponent<T> {
  private static readonly SCROLL_EDGE_THRESHOLD: number = 5;
  private static readonly DEBOUNCE_DELAY_MS: number = 500;

  readonly scrollableChange: OutputEmitterRef<boolean> = output<boolean>();
  readonly scrollable: Signal<ScrollableDirective> = viewChild(ScrollableDirective);

  readonly tabsQueryList: Signal<ReadonlyArray<TabsComponent<T>>> = contentChildren(TabsComponent, { descendants: true });

  readonly showPrevButton: WritableSignal<boolean> = signal(false);
  readonly showNextButton: WritableSignal<boolean> = signal(false);

  private readonly viewportRuler = inject(ViewportRuler);
  private readonly destroyRef = inject(DestroyRef);

  private scrollSize: number = 600;

  constructor() {
    // effect: wires scroll event subscriptions to the CDK Scrollable directive and
    // ViewportRuler — both are imperative APIs; onCleanup unsubscribes on each re-run
    // so that stale subscriptions are not accumulated when tabs change.
    effect((onCleanup: EffectCleanupRegisterFn) => {
      const scrollable = this.scrollable();
      const tabsList = this.tabsQueryList();

      if (scrollable && tabsList.length > 0) {
        const tabsComponent = tabsList[0];
        // Track tabs in the main effect
        tabsComponent.tabs();

        const subscriptions = this.initScrollLogic(scrollable, tabsComponent);
        onCleanup((): void => {
          subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
        });
      }
    });

    // The synchronous measurement inside the effect above can run before the projected tab
    // content (e.g. translated labels) has settled to its final rendered width, so the very
    // first scroll-button computation can be stale. Re-check once after the browser has
    // committed the first render to catch that case.
    afterNextRender((): void => {
      this.setScrollSize();
      this.scrollHandler();
    });
  }

  private initScrollLogic(scrollable: ScrollableDirective, tabsComponent: TabsComponent<T>): Array<Subscription> {
    const subscriptions: Array<Subscription> = [];

    this.scrollHandler();

    subscriptions.push(scrollable.elementScrolled().subscribe((): void => {
      this.scrollHandler();
    }));

    subscriptions.push(this.viewportRuler.change(TabsScrollComponent.DEBOUNCE_DELAY_MS).subscribe((): void => {
      this.setScrollSize();
      this.scrollHandler();
    }));

    subscriptions.push(outputToObservable(tabsComponent.onStatusChange).pipe(
      filter((event: TabStatusChangeEvent<T>): boolean => event.eventType === 'afterSelect'))
      .subscribe((event: TabStatusChangeEvent<T>): void => {
        this.scrollToTabIfNeeded(event.tabId);
      }));

    this.setScrollSize();

    return subscriptions;
  }

  next(): void {
    this.scroll(HorizontalScrollDirection.RIGHT);
  }

  previous(): void {
    this.scroll(HorizontalScrollDirection.LEFT);
  }

  isScrollable(): boolean {
    return this.showPrevButton() || this.showNextButton();
  }

  scrollToTabIfNeeded(tabId: T): void {
    const tabs = this.getTabsComponent();
    if (!tabs) return;

    const tab = tabs.tabs().find((component: TabComponent<T>): boolean => component.getTabId() === tabId);
    if (tab !== undefined) {
      const tabRect = tab.getBoundingClientRect();
      const scrollable = this.scrollable();
      if (!scrollable) return;

      const scrollableElementRect = scrollable.getElementRect();
      if (tabRect.right > scrollableElementRect.right && this.showNextButton()) {
        const distance = tabRect.right - scrollableElementRect.left;
        if (distance > this.scrollSize) {
          scrollable.scrollTo({ right: this.getScrollRightEndPoint(tabRect, scrollableElementRect), behavior: 'smooth' });
        }
      } else if (tabRect.left < scrollableElementRect.left && this.showPrevButton()) {
        scrollable.scrollTo({ left: this.getScrollLeftEndPoint(tabRect, scrollableElementRect), behavior: 'smooth' });
      }
    }
  }

  private scrollHandler(): void {
    const scrollable = this.scrollable();
    if (!scrollable) return;

    const leftScrollOffset = scrollable.measureScrollOffset('left');
    const rightScrollOffset = scrollable.measureScrollOffset('right');
    const scrollableActiveBefore = this.isScrollable();

    this.showPrevButton.set(leftScrollOffset > TabsScrollComponent.SCROLL_EDGE_THRESHOLD);
    this.showNextButton.set(rightScrollOffset > TabsScrollComponent.SCROLL_EDGE_THRESHOLD);

    if (scrollableActiveBefore !== this.isScrollable()) {
      if (!this.destroyRef.destroyed) {
        this.scrollableChange.emit(this.isScrollable());
      }
    }
  }

  private scroll(direction: HorizontalScrollDirection, scrollOffset: number = undefined): void {
    const scrollable = this.scrollable();
    if (!scrollable) return;

    if (scrollOffset === undefined) {
      scrollOffset = scrollable.measureScrollOffset(direction);
    }

    if (scrollOffset > TabsScrollComponent.SCROLL_EDGE_THRESHOLD) {
      const scrollEndPoint = scrollOffset < this.scrollSize ? 0 : scrollOffset - this.scrollSize;
      if (direction === HorizontalScrollDirection.LEFT) {
        scrollable.scrollTo({ left: scrollEndPoint, behavior: 'smooth' });
      } else {
        scrollable.scrollTo({ right: scrollEndPoint, behavior: 'smooth' });
      }
    }
  }

  private setScrollSize(): void {
    const scrollable = this.scrollable();
    if (!scrollable) return;

    const elementRect = scrollable.getElementRect();
    if (elementRect !== undefined) {
      this.scrollSize = elementRect.width;
    }
  }

  private getScrollRightEndPoint(tabRect: DOMRect, scrollableElementRect: DOMRect): number {
    const scrollable = this.scrollable();
    const scrollOffsetRight = scrollable.measureScrollOffset('right');
    const scrollEndPoint = scrollableElementRect.right + scrollOffsetRight - tabRect.left + tabRect.width - scrollableElementRect.width;
    return scrollEndPoint < 0 ? 0 : scrollEndPoint;
  }

  private getScrollLeftEndPoint(tabRect: DOMRect, scrollableElementRect: DOMRect): number {
    const scrollable = this.scrollable();
    const scrollOffsetLeft = scrollable.measureScrollOffset('left');
    const scrollEndPoint = scrollOffsetLeft - scrollableElementRect.left + tabRect.left - tabRect.width;
    return scrollEndPoint < 0 ? 0 : scrollEndPoint;
  }

  private verifyTabsList(): void {
    const tabsArray = this.tabsQueryList();
    if (tabsArray.length !== 1) {
      throw new Error(`TabsScrollComponent doesn't support multiple nested TabsComponent, the actual size is ${tabsArray.length}`);
    }
  }

  private getTabsComponent(): TabsComponent<T> {
    this.verifyTabsList();
    return this.tabsQueryList()[0];
  }
}
